import React, { useMemo } from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import {
    isPresent, isDefined,
    extractBannerImagesFromDraftContent,
} from '../helpers';
import { getPage } from '../selectors';
import RenderContent from './ui/RenderContent';

function normalizeHref(raw) {
    if (!isPresent(raw)) return '';
    const v = `${raw}`.trim();
    if (/^(https?:)?\/\//i.test(v)) return v.startsWith('//') ? `https:${v}` : v;
    if (/^(mailto:|tel:)/i.test(v)) return v;
    return `https://${v.replace(/^\/+/, '')}`;
}

function BannerBlockContent({ content }) {
    const images = useMemo(
        () => extractBannerImagesFromDraftContent(content),
        [content],
    );

    if (!images.length) {
        if (!isDefined(content)) return null;
        return <RenderContent content={content} />;
    }

    return images.map((img, i) => {
        const imageEl = (
            <img
                className="cd-image"
                src={img.src}
                alt={img.alt || ''}
                loading="eager"
                decoding="async"
                style={{
                    width: img.width || 'auto',
                    height: img.height || 'auto',
                }}
            />
        );
        if (isPresent(img.href)) {
            return (
                <a
                    key={img.src || i}
                    href={normalizeHref(img.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {imageEl}
                </a>
            );
        }
        return React.cloneElement(imageEl, { key: img.src || i });
    });
}

function BannerBlock({ block }) {
    const items = [];
    let style = {};
    const subblocks = (block.get('blocks') || Immutable.List()).sortBy(
        (sb) => sb.get('order'),
    );

    if (subblocks.size > 0) {
        subblocks.forEach((sb) => {
            items.push(<BannerBlock block={sb} key={sb.get('slug')} />);
        });
    }
    if (isDefined(block.get('style'))) {
        const st = (block.get('style') || Immutable.Map()).toJS();
        if (isPresent(st.backgroundImage)) {
            st.backgroundImage = `url("${st.backgroundImage}")`;
        }
        style = { ...style, ...st };
    }
    const cn = block.get('class_names');
    const body = block.get('body');
    return (
        <div className={['cd-block', cn].filter(Boolean).join(' ')} key={block.get('slug')} style={style}>
            {isDefined(body) ? (
                <div className="cd-text">
                    <BannerBlockContent content={body} />
                </div>
            ) : null}
            {items.length > 0 ? (
                <div className="cd-container">
                    {items}
                </div>
            ) : null}
        </div>
    );
}

/** Lightweight sidebar banners — avoids Draftail boot for image blocks. */
export default function SidebarBanner({ slug }) {
    const page = useSelector((state) => getPage(state, slug));
    if (!page || !page.get) {
        return null;
    }
    const blocks = (page.get('blocks') || Immutable.List()).sortBy(
        (b) => b.get('order'),
    );

    return blocks.map((block) => (
        <BannerBlock block={block} key={block.get('slug')} />
    ));
}
